import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useTranslation } from '../../i18n';
import {
    FaBell,
    FaSync,
    FaExclamationTriangle,
    FaExternalLinkAlt,
    FaFilter,
    FaCheckSquare,
    FaSquare,
    FaInfoCircle,
    FaFileUpload,
    FaArrowUp,
    FaArrowDown,
    FaDownload,
    FaTrash,
    FaToggleOn,
    FaToggleOff,
    FaCheckCircle,
    FaTimesCircle,
    FaHistory,
    FaChartLine,
    FaCalendarAlt,
    FaTimes,
    FaSortAmountDown,
    FaSortAmountUp,
    FaEye,
    FaThLarge,
    FaTable,
    FaTags,
    FaTag,
    FaFileExcel
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';
import useActivityTracker from '../../hooks/useActivityTracker';
import './AlertsPage.css';

// Registrar componentes de Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    ChartTooltip,
    Legend
);

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

// Sanea el rango de variacion (sobre valor absoluto): min >= 0, max > 0, min <= max.
// min vacio -> 0 (incluye productos sin cambio). max vacio/invalido -> 66 (tope de outliers).
const sanitizeRange = (rawMin, rawMax) => {
    let min = Number(rawMin);
    let max = Number(rawMax);
    if (!Number.isFinite(min) || min < 0) min = 0;
    if (!Number.isFinite(max) || max <= 0) max = 66;
    min = Math.min(min, 100);
    max = Math.min(max, 1000);
    if (min > max) min = max; // rango invertido -> band = [max, max]
    return { min, max };
};

const AlertsPage = () => {
    const { user } = useAuthenticator((context) => [context.user]);
    const { t, language } = useTranslation();
    const { trackAction } = useActivityTracker(user?.username, 'price_monitor');
    const [alerts, setAlerts] = useState({});
    const [summary, setSummary] = useState({ total_alerts: 0, chains: [], threshold: 5, mode: 'default' });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);

    // Configuracion — el filtro es un RANGO sobre el valor absoluto de la variacion:
    // min <= ABS(variacion %) <= max. Ej: min=0 max=10 => todo lo que vario dentro de +-10%.
    const [threshold, setThreshold] = useState(5);   // variacion minima (%)
    const [maxChange, setMaxChange] = useState(66);  // variacion maxima (%) — antes era un tope oculto
    // Rango realmente aplicado en la ultima carga (para que el resumen no dependa del input sin aplicar)
    const [appliedRange, setAppliedRange] = useState({ min: 5, max: 66 });
    const [limitPerChain, setLimitPerChain] = useState(10);

    // Filtro de UPCs - toggle simple
    const [filterByUpcs, setFilterByUpcs] = useState(false);

    // Filtro de canales
    const [selectedChains, setSelectedChains] = useState(new Set());
    const [showChainFilter, setShowChainFilter] = useState(false);

    // Ordenamiento de variaciones
    const [sortOrder, setSortOrder] = useState('highest'); // 'highest', 'lowest', 'positive', 'negative'

    // Mostrar todos los UPCs prioritarios (incluso sin variación)
    const [showAllTrackedUpcs, setShowAllTrackedUpcs] = useState(false);

    // UPCs tracked
    const [trackedUpcs, setTrackedUpcs] = useState([]);
    const [trackingFileExists, setTrackingFileExists] = useState(false);
    const [showUpcManager, setShowUpcManager] = useState(false);
    const [newUpcs, setNewUpcs] = useState('');
    const [savingUpcs, setSavingUpcs] = useState(false);
    const [upcError, setUpcError] = useState(null);
    const [upcSuccess, setUpcSuccess] = useState(null);
    const fileInputRef = useRef(null);

    // Info panel
    const [showInfo, setShowInfo] = useState(false);

    // Vista: 'grid' (tarjetas) o 'table' (tabla). Se persiste en localStorage.
    const [viewMode, setViewMode] = useState(() => {
        try { return localStorage.getItem('alerts_view_mode') || 'grid'; } catch { return 'grid'; }
    });

    // Base de variacion: 'final' (Precio Final) o 'full' (Precio Regular). Persistida.
    const [priceBasis, setPriceBasis] = useState(() => {
        try { return localStorage.getItem('alerts_price_basis') || 'final'; } catch { return 'final'; }
    });

    // Persistencia del toggle "Filtrar por UPCs"
    const [savingFilterUpcsSetting, setSavingFilterUpcsSetting] = useState(false);

    // Historial de precios modal
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyProduct, setHistoryProduct] = useState(null);
    const [priceHistory, setPriceHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Ref para evitar loops en useEffect
    const isInitialMount = useRef(true);

    // Funcion para cargar alertas - usa parametros directos en vez de estado
    const loadAlerts = useCallback(async (useUpcFilter = false, includeAllTracked = false) => {
        if (!user?.username) return;

        try {
            setLoading(true);
            setError(null);

            // Determinar el modo basado en los filtros
            let mode = 'default';
            if (useUpcFilter && includeAllTracked) {
                mode = 'tracked_all'; // Todos los UPCs prioritarios, con o sin variación
            } else if (useUpcFilter) {
                mode = 'tracked'; // Solo UPCs prioritarios con variación
            }

            // Rango de variacion (sobre valor absoluto). Min puede ser 0 (incluir sin cambio).
            const { min: safeMin, max: safeMax } = sanitizeRange(threshold, maxChange);
            setAppliedRange({ min: safeMin, max: safeMax });

            const params = new URLSearchParams({
                username: user.username,
                threshold: safeMin.toString(),
                max_change: safeMax.toString(),
                limit: limitPerChain.toString(),
                mode: mode,
                price_basis: priceBasis
            });

            console.log('[ALERTS] Cargando con mode:', mode, 'basis:', priceBasis, 'rango:', safeMin, '-', safeMax);

            const response = await fetch(`${STRANDS_API_URL}/api/price-alerts?${params}`);
            const data = await response.json();

            if (data.success) {
                setAlerts(data.alerts || {});
                setSummary(data.summary || { total_alerts: 0, chains: [] });
                setLastUpdate(new Date());

                // Seleccionar todos los canales por defecto
                const allChains = Object.keys(data.alerts || {});
                setSelectedChains(new Set(allChains));
            } else {
                setError(data.error || data.message || 'Error al cargar alertas');
            }
        } catch (err) {
            console.error('[ALERTS] Error:', err);
            setError('Error de conexion al servidor');
        } finally {
            setLoading(false);
        }
    }, [user?.username, threshold, maxChange, limitPerChain, priceBasis]);

    const fetchTrackedUpcs = useCallback(async () => {
        if (!user?.username) return;

        try {
            const response = await fetch(
                `${STRANDS_API_URL}/api/price-alerts/tracked-upcs?username=${encodeURIComponent(user.username)}`
            );
            const data = await response.json();

            if (data.success) {
                setTrackedUpcs(data.upcs || []);
                setTrackingFileExists(data.upcs && data.upcs.length > 0);
            }
        } catch (err) {
            console.error('[ALERTS] Error loading tracked UPCs:', err);
        }
    }, [user?.username]);

    const saveTrackedUpcs = async (upcsToSave = null, replaceAll = false) => {
        if (!user?.username) return;

        const newUpcsArray = upcsToSave || newUpcs.split(/[\n,]/).map(u => u.trim()).filter(u => u);

        if (newUpcsArray.length === 0) {
            setUpcError('No hay UPCs para agregar');
            return;
        }

        // Concatenar con los existentes (sin duplicados) a menos que sea reemplazo total
        let finalUpcs;
        if (replaceAll) {
            finalUpcs = newUpcsArray;
        } else {
            const existingSet = new Set(trackedUpcs);
            const addedUpcs = newUpcsArray.filter(upc => !existingSet.has(upc));
            finalUpcs = [...trackedUpcs, ...addedUpcs];

            if (addedUpcs.length === 0) {
                setUpcError('Todos los UPCs ya existen en la lista');
                return;
            }
        }

        setSavingUpcs(true);
        setUpcError(null);
        setUpcSuccess(null);

        try {
            const response = await fetch(`${STRANDS_API_URL}/api/price-alerts/tracked-upcs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: user.username,
                    upcs: finalUpcs
                })
            });

            const data = await response.json();

            if (data.success) {
                const addedCount = data.upcs.length - trackedUpcs.length;
                trackAction('save_tracked_upcs', {
                    total: data.upcs.length,
                    added: addedCount,
                    replace_all: replaceAll
                });
                setTrackedUpcs(data.upcs);
                setTrackingFileExists(true);
                setNewUpcs('');

                if (replaceAll) {
                    setUpcSuccess(`${data.upcs.length} UPCs guardados (reemplazo total)`);
                } else {
                    setUpcSuccess(`${addedCount} UPCs agregados. Total: ${data.upcs.length}`);
                }

                // Activar filtro y recargar con filtro activo
                setFilterByUpcs(true);
                saveFilterByUpcsSetting(true);
                loadAlerts(true);
            } else {
                setUpcError(data.error || data.message || 'Error al guardar UPCs');
            }
        } catch (err) {
            console.error('[ALERTS] Error saving UPCs:', err);
            setUpcError('Error de conexion al guardar UPCs');
        } finally {
            setSavingUpcs(false);
        }
    };

    const clearTrackedUpcs = async () => {
        if (!user?.username) return;

        if (!window.confirm('¿Estas seguro de eliminar todos los UPCs monitoreados?')) {
            return;
        }

        setSavingUpcs(true);
        setUpcError(null);
        setUpcSuccess(null);

        try {
            const response = await fetch(`${STRANDS_API_URL}/api/price-alerts/tracked-upcs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: user.username,
                    upcs: []
                })
            });

            const data = await response.json();

            if (data.success) {
                trackAction('clear_tracked_upcs');
                setTrackedUpcs([]);
                setTrackingFileExists(false);
                setNewUpcs('');
                setUpcSuccess('UPCs eliminados. El archivo quedara vacio.');
                setFilterByUpcs(false);
                saveFilterByUpcsSetting(false);
                loadAlerts(false);
            } else {
                setUpcError(data.error || 'Error al limpiar UPCs');
            }
        } catch (err) {
            console.error('[ALERTS] Error clearing UPCs:', err);
            setUpcError('Error de conexion al limpiar UPCs');
        } finally {
            setSavingUpcs(false);
        }
    };

    const downloadTemplate = () => {
        const csvContent = "upc\n7501082243741\n7501082243727\n7501082243710\n";
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'upc_tracking_template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setUpcError(null);
        setUpcSuccess(null);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const text = e.target.result;
                const lines = text.split(/[\r\n]+/).filter(line => line.trim());

                const headerLine = lines[0].toLowerCase();
                let upcIndex = -1;

                if (headerLine.includes(',')) {
                    const headers = headerLine.split(',').map(h => h.trim());
                    upcIndex = headers.findIndex(h => h === 'upc' || h === 'codigo' || h === 'ean');
                } else if (headerLine === 'upc' || headerLine === 'codigo' || headerLine === 'ean') {
                    upcIndex = 0;
                }

                let upcs = [];

                if (upcIndex >= 0) {
                    for (let i = 1; i < lines.length; i++) {
                        const cols = lines[i].split(',');
                        const upc = cols[upcIndex]?.trim();
                        if (upc && /^\d+$/.test(upc)) {
                            upcs.push(upc);
                        }
                    }
                } else {
                    for (const line of lines) {
                        const upc = line.trim().split(',')[0].trim();
                        if (upc && /^\d+$/.test(upc)) {
                            upcs.push(upc);
                        }
                    }
                }

                if (upcs.length === 0) {
                    setUpcError('No se encontraron UPCs validos en el archivo. Asegurate de que tenga una columna "upc" con codigos numericos.');
                    return;
                }

                setNewUpcs(upcs.join('\n'));
                setUpcSuccess(`${upcs.length} UPCs cargados del archivo. Presiona "Guardar y Aplicar" para guardarlos en S3.`);

            } catch (err) {
                console.error('[ALERTS] Error parsing file:', err);
                setUpcError('Error al leer el archivo. Verifica que sea un CSV valido.');
            }
        };

        reader.onerror = () => {
            setUpcError('Error al leer el archivo');
        };

        reader.readAsText(file);
        event.target.value = '';
    };

    // Persistir el estado del filtro de UPCs en la BD (best-effort, no bloquea la UI)
    const saveFilterByUpcsSetting = useCallback(async (newValue) => {
        if (!user?.username) return;
        setSavingFilterUpcsSetting(true);
        try {
            await fetch(
                `${STRANDS_API_URL}/api/user/price-alerts-filter-upcs?username=${encodeURIComponent(user.username)}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ enabled: newValue })
                }
            );
        } catch (err) {
            console.error('[ALERTS] Error saving filter-by-upcs setting:', err);
        } finally {
            setSavingFilterUpcsSetting(false);
        }
    }, [user?.username]);

    // Toggle filtro de UPCs - recarga con el nuevo valor y persiste la preferencia
    const toggleUpcFilter = () => {
        const newValue = !filterByUpcs;
        setFilterByUpcs(newValue);
        saveFilterByUpcsSetting(newValue);
        // Si se desactiva el filtro de UPCs, también desactivar "ver todos"
        if (!newValue) {
            setShowAllTrackedUpcs(false);
            loadAlerts(false, false);
        } else {
            loadAlerts(true, showAllTrackedUpcs);
        }
    };

    // Toggle para mostrar todos los UPCs prioritarios (con o sin variación)
    const toggleShowAllTracked = () => {
        const newValue = !showAllTrackedUpcs;
        setShowAllTrackedUpcs(newValue);
        loadAlerts(filterByUpcs, newValue);
    };

    // Handler para boton Aplicar
    const handleApply = () => {
        loadAlerts(filterByUpcs, showAllTrackedUpcs);
    };

    // Cargar historial de precios de un producto
    const loadPriceHistory = async (product, chainName) => {
        if (!user?.username) return;

        setHistoryProduct({ ...product, chainName });
        setShowHistoryModal(true);
        setHistoryLoading(true);
        setPriceHistory([]);

        try {
            const params = new URLSearchParams({
                username: user.username,
                upc: product.upc,
                canal: chainName,
                limit: '5'
            });

            const response = await fetch(`${STRANDS_API_URL}/api/price-history?${params}`);
            const data = await response.json();

            if (data.success) {
                setPriceHistory(data.history_chart || []);
            }
        } catch (err) {
            console.error('[ALERTS] Error loading price history:', err);
        } finally {
            setHistoryLoading(false);
        }
    };

    // Cambiar vista grid/tabla (persistido en localStorage, sin recargar datos)
    const changeViewMode = (mode) => {
        setViewMode(mode);
        try { localStorage.setItem('alerts_view_mode', mode); } catch { /* ignore */ }
    };

    // Cambiar base de variacion (final/full) -> persiste y recarga desde el backend
    const changePriceBasis = (basis) => {
        if (basis === priceBasis) return;
        setPriceBasis(basis);
        try { localStorage.setItem('alerts_price_basis', basis); } catch { /* ignore */ }
        // loadAlerts depende de priceBasis; usar el nuevo valor directo para no esperar al re-render
        loadAlertsWithBasis(basis);
    };

    // Variante de loadAlerts que fuerza un basis concreto (evita el estado stale)
    const loadAlertsWithBasis = (basis) => {
        if (!user?.username) return;
        setLoading(true);
        setError(null);

        let mode = 'default';
        if (filterByUpcs && showAllTrackedUpcs) mode = 'tracked_all';
        else if (filterByUpcs) mode = 'tracked';

        const { min: safeMin, max: safeMax } = sanitizeRange(threshold, maxChange);
        setAppliedRange({ min: safeMin, max: safeMax });
        const params = new URLSearchParams({
            username: user.username,
            threshold: safeMin.toString(),
            max_change: safeMax.toString(),
            limit: limitPerChain.toString(),
            mode,
            price_basis: basis
        });

        fetch(`${STRANDS_API_URL}/api/price-alerts?${params}`)
            .then(r => r.json())
            .then(data => {
                if (data.success) {
                    setAlerts(data.alerts || {});
                    setSummary(data.summary || { total_alerts: 0, chains: [] });
                    setLastUpdate(new Date());
                    setSelectedChains(new Set(Object.keys(data.alerts || {})));
                } else {
                    setError(data.error || data.message || 'Error al cargar alertas');
                }
            })
            .catch(err => {
                console.error('[ALERTS] Error:', err);
                setError('Error de conexion al servidor');
            })
            .finally(() => setLoading(false));
    };

    // Aplicar el umbral (Enter o al salir del campo) -> recarga con el valor actual
    const applyThreshold = () => {
        loadAlerts(filterByUpcs, showAllTrackedUpcs);
    };

    // Efecto inicial - solo se ejecuta una vez al montar
    useEffect(() => {
        if (!user?.username) return;

        // Cargar preferencia persistida del filtro y luego pedir alertas con ese valor
        // para evitar un doble fetch (una con filtro off y otra con filtro on).
        (async () => {
            let savedFilter = false;
            try {
                const resp = await fetch(
                    `${STRANDS_API_URL}/api/user/price-alerts-filter-upcs?username=${encodeURIComponent(user.username)}`
                );
                const data = await resp.json();
                if (data.success) {
                    savedFilter = !!data.enabled;
                    setFilterByUpcs(savedFilter);
                }
            } catch (err) {
                console.error('[ALERTS] Error loading filter-by-upcs setting:', err);
            }
            loadAlerts(savedFilter, false);
        })();

        fetchTrackedUpcs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.username]);

    // Filtrar y ordenar productos GLOBALMENTE por porcentaje de variación
    const filteredAlerts = useMemo(() => {
        // Aplanar todos los productos de todas las cadenas en un solo array
        let allProducts = [];

        Object.entries(alerts).forEach(([chainName, products]) => {
            if (selectedChains.has(chainName)) {
                // Agregar el nombre de la cadena a cada producto
                const productsWithChain = products.map(p => ({
                    ...p,
                    chainName: chainName
                }));
                allProducts = allProducts.concat(productsWithChain);
            }
        });

        // Filtrar por tipo de variación (positivo/negativo)
        if (sortOrder === 'positive') {
            allProducts = allProducts.filter(p => (p.price_change_pct || 0) > 0);
        } else if (sortOrder === 'negative') {
            allProducts = allProducts.filter(p => (p.price_change_pct || 0) < 0);
        }

        // Ordenar GLOBALMENTE según el criterio seleccionado
        switch (sortOrder) {
            case 'highest':
            case 'positive':
            case 'negative':
                // Mayor variación absoluta primero
                allProducts.sort((a, b) => Math.abs(b.price_change_pct || 0) - Math.abs(a.price_change_pct || 0));
                break;
            case 'lowest':
                // Menor variación absoluta primero
                allProducts.sort((a, b) => Math.abs(a.price_change_pct || 0) - Math.abs(b.price_change_pct || 0));
                break;
            default:
                break;
        }

        return allProducts;
    }, [alerts, selectedChains, sortOrder]);

    // Contar productos filtrados (ahora es un array plano)
    const filteredCount = useMemo(() => {
        return filteredAlerts.length;
    }, [filteredAlerts]);

    const toggleChainSelection = (chain) => {
        setSelectedChains(prev => {
            const newSet = new Set(prev);
            if (newSet.has(chain)) {
                newSet.delete(chain);
            } else {
                newSet.add(chain);
            }
            return newSet;
        });
    };

    // Descarga en Excel exactamente lo que se ve: mismas cadenas, mismo filtro
    // de aumentos/bajas y mismo orden. Una segunda hoja deja constancia de los
    // filtros con los que se generó.
    const downloadExcel = () => {
        if (!filteredAlerts.length) return;

        const variationHeader = `${t('alerts.colVariation')} (%)`;
        const rows = filteredAlerts.map(p => ({
            [t('alerts.colChain')]: p.chainName,
            [t('alerts.colProduct')]: p.item || '',
            UPC: p.upc || '',
            [t('alerts.colPrevious')]: p.last_price ?? null,
            [t('alerts.colCurrent')]: p.final_price ?? null,
            [variationHeader]: p.price_change_pct != null ? Math.round(p.price_change_pct * 10) / 10 : null,
            [t('alerts.date')]: p.date || '',
            URL: p.url_sku || '',
        }));
        const alertsSheet = XLSX.utils.json_to_sheet(rows);
        alertsSheet['!cols'] = [
            { wch: 24 }, { wch: 60 }, { wch: 18 }, { wch: 12 },
            { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 50 }
        ];

        const sortLabels = {
            highest: t('alerts.sortHighest'),
            lowest: t('alerts.sortLowest'),
            positive: t('alerts.sortPositive'),
            negative: t('alerts.sortNegative'),
        };
        const locale = language === 'es' ? 'es-MX' : language === 'pt' ? 'pt-BR' : 'en-US';
        const filtersSheet = XLSX.utils.aoa_to_sheet([
            [t('alerts.excelFilter'), t('alerts.excelValue')],
            [t('alerts.excelRange'), `${appliedRange.min}% - ${appliedRange.max}%`],
            [t('alerts.excelBasis'), priceBasis === 'full' ? t('alerts.basisFull') : t('alerts.basisFinal')],
            [t('alerts.excelSort'), sortLabels[sortOrder] || sortOrder],
            [t('alerts.excelChains'), [...selectedChains].sort().join(', ')],
            [t('alerts.excelUpcFilter'), filterByUpcs ? t('alerts.excelYes') : t('alerts.excelNo')],
            [t('alerts.excelRows'), `${filteredAlerts.length} / ${summary.total_alerts}`],
            [t('alerts.excelDownloadedAt'), new Date().toLocaleString(locale)],
        ]);
        filtersSheet['!cols'] = [{ wch: 24 }, { wch: 80 }];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, alertsSheet, t('alerts.excelSheetAlerts'));
        XLSX.utils.book_append_sheet(workbook, filtersSheet, t('alerts.excelSheetFilters'));
        const today = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `alertas_precios_${today}.xlsx`);

        trackAction('export_excel', {
            rows: filteredAlerts.length,
            view: viewMode,
            basis: priceBasis,
        });
    };

    const selectAllChains = () => {
        setSelectedChains(new Set(Object.keys(alerts)));
    };

    const deselectAllChains = () => {
        setSelectedChains(new Set());
    };

    const formatPrice = (price) => {
        if (price === null || price === undefined) return '-';
        return `$${price.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatChange = (change) => {
        const sign = change > 0 ? '+' : '';
        return `${sign}${change.toFixed(1)}%`;
    };

    const formatDate = (date) => {
        return date.toLocaleString('es-MX', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Formatear fecha de extracción (viene como string del backend)
    const formatExtractionDate = (dateStr) => {
        if (!dateStr) return t('alerts.lastRecordedDate') || 'Última fecha registrada';
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) {
                return t('alerts.lastRecordedDate') || 'Última fecha registrada';
            }
            return date.toLocaleDateString(language === 'es' ? 'es-MX' : language === 'pt' ? 'pt-BR' : 'en-US', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch {
            return t('alerts.lastRecordedDate') || 'Última fecha registrada';
        }
    };

    // Formatear fecha corta para gráfica
    const formatShortDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return '-';
            return date.toLocaleDateString(language === 'es' ? 'es-MX' : language === 'pt' ? 'pt-BR' : 'en-US', {
                day: '2-digit',
                month: 'short'
            });
        } catch {
            return '-';
        }
    };

    return (
        <div className="alerts-page">
            {/* Header */}
            <div className="alerts-header">
                <div className="alerts-header-left">
                    <FaBell className="alerts-icon" />
                    <div>
                        <h1 className="alerts-title">{t('alerts.title')}</h1>
                        <p className="alerts-subtitle">
                            {t('alerts.subtitle')}
                        </p>
                    </div>
                </div>

                <div className="alerts-header-right">
                    {lastUpdate && (
                        <span className="alerts-last-update">
                            {formatDate(lastUpdate)}
                        </span>
                    )}
                    <button
                        className="alerts-btn-icon"
                        onClick={() => setShowInfo(true)}
                        title={t('alerts.info')}
                    >
                        <FaInfoCircle />
                    </button>
                    <button
                        className="alerts-btn-primary"
                        onClick={() => loadAlerts(filterByUpcs, showAllTrackedUpcs)}
                        disabled={loading}
                    >
                        <FaSync className={loading ? 'spinning' : ''} />
                        {t('alerts.update')}
                    </button>
                </div>
            </div>

            {/* Config Panel - Always visible */}
            <div className="alerts-config-panel">
                <div className="config-row">
                    <div className="config-group">
                        <label>{t('alerts.minThreshold')}</label>
                        <input
                            type="number"
                            value={threshold}
                            onChange={(e) => setThreshold(e.target.value === '' ? '' : Number(e.target.value))}
                            onBlur={() => {
                                if (threshold === '' || Number(threshold) < 0) setThreshold(0);
                                applyThreshold();
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                            min="0"
                            max="100"
                            title={t('alerts.minThresholdHint')}
                        />
                    </div>
                    <div className="config-group">
                        <label>{t('alerts.maxThreshold')}</label>
                        <input
                            type="number"
                            value={maxChange}
                            onChange={(e) => setMaxChange(e.target.value === '' ? '' : Number(e.target.value))}
                            onBlur={() => {
                                if (maxChange === '' || Number(maxChange) <= 0) setMaxChange(66);
                                applyThreshold();
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                            min="1"
                            max="1000"
                            title={t('alerts.maxThresholdHint')}
                        />
                    </div>
                    <div className="config-group">
                        <label>{t('alerts.productsPerChain')}</label>
                        <input
                            type="number"
                            value={limitPerChain}
                            onChange={(e) => setLimitPerChain(Math.min(Number(e.target.value), 1000))}
                            min="1"
                            max="1000"
                        />
                    </div>

                    {/* Selector de ordenamiento */}
                    <div className="config-group">
                        <label>{t('alerts.sortBy')}</label>
                        <select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            className="sort-select"
                        >
                            <option value="highest">{t('alerts.sortHighest')}</option>
                            <option value="lowest">{t('alerts.sortLowest')}</option>
                            <option value="positive">{t('alerts.sortPositive')}</option>
                            <option value="negative">{t('alerts.sortNegative')}</option>
                        </select>
                    </div>

                    {/* Toggle para filtro de UPCs */}
                    <div className="config-group config-toggle-group">
                        <label>{t('alerts.filterByUpcs')}</label>
                        <button
                            className={`upc-toggle-btn ${filterByUpcs ? 'active' : ''}`}
                            onClick={toggleUpcFilter}
                            disabled={trackedUpcs.length === 0 || savingFilterUpcsSetting}
                            title={trackedUpcs.length === 0 ? t('alerts.noUpcsConfigured') : (filterByUpcs ? t('alerts.viewAllProducts') : t('alerts.filterOnlyMyUpcs'))}
                        >
                            {filterByUpcs ? <FaToggleOn /> : <FaToggleOff />}
                            <span>{filterByUpcs ? t('alerts.enabled') : t('alerts.disabled')}</span>
                        </button>
                    </div>

                    {/* Toggle para ver todos los UPCs prioritarios (solo visible cuando filterByUpcs está activo) */}
                    {filterByUpcs && (
                        <div className="config-group config-toggle-group">
                            <label>{t('alerts.showAllTracked')}</label>
                            <button
                                className={`upc-toggle-btn ${showAllTrackedUpcs ? 'active' : ''}`}
                                onClick={toggleShowAllTracked}
                                title={showAllTrackedUpcs ? t('alerts.showOnlyWithChanges') : t('alerts.showAllTrackedUpcs')}
                            >
                                {showAllTrackedUpcs ? <FaEye /> : <FaToggleOff />}
                                <span>{showAllTrackedUpcs ? t('alerts.all') : t('alerts.onlyChanges')}</span>
                            </button>
                        </div>
                    )}

                    <button className="config-apply-btn" onClick={handleApply}>
                        {t('alerts.apply')}
                    </button>
                    <button className="config-upcs-btn" onClick={() => {
                        setShowUpcManager(true);
                        setUpcError(null);
                        setUpcSuccess(null);
                    }}>
                        <FaFileUpload /> {t('alerts.upcs')} ({trackedUpcs.length})
                    </button>

                    {/* Base de variacion: Precio Final vs Precio Regular (full price) */}
                    <div className="config-group">
                        <label>{t('alerts.priceBasisLabel')}</label>
                        <div className="basis-toggle">
                            <button
                                className={`basis-btn ${priceBasis === 'final' ? 'active' : ''}`}
                                onClick={() => changePriceBasis('final')}
                                title={t('alerts.basisFinalHint')}
                            >
                                <FaTag /> {t('alerts.basisFinal')}
                            </button>
                            <button
                                className={`basis-btn ${priceBasis === 'full' ? 'active' : ''}`}
                                onClick={() => changePriceBasis('full')}
                                title={t('alerts.basisFullHint')}
                            >
                                <FaTags /> {t('alerts.basisFull')}
                            </button>
                        </div>
                    </div>

                    {/* Vista: tarjetas o tabla */}
                    <div className="config-group">
                        <label>{t('alerts.viewMode')}</label>
                        <div className="view-toggle">
                            <button
                                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                                onClick={() => changeViewMode('grid')}
                                title={t('alerts.viewGrid')}
                                aria-label={t('alerts.viewGrid')}
                            >
                                <FaThLarge />
                            </button>
                            <button
                                className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
                                onClick={() => changeViewMode('table')}
                                title={t('alerts.viewTable')}
                                aria-label={t('alerts.viewTable')}
                            >
                                <FaTable />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="alerts-content">
                {/* Loading State */}
                {loading && (
                    <div className="alerts-loading">
                        <div className="alerts-spinner"></div>
                        <p>{t('alerts.analyzingPriceChanges')}</p>
                    </div>
                )}

                {/* Error State */}
                {error && !loading && (
                    <div className="alerts-error">
                        <FaExclamationTriangle />
                        <p>{error}</p>
                        <button onClick={() => loadAlerts(filterByUpcs, showAllTrackedUpcs)}>{t('alerts.retry')}</button>
                    </div>
                )}

                {/* Empty State */}
                {!loading && !error && summary.total_alerts === 0 && (
                    <div className="alerts-empty">
                        <FaBell className="empty-icon" />
                        <h3>{t('alerts.noPriceAlerts')}</h3>
                        <p>
                            {filterByUpcs
                                ? t('alerts.noChangesInTracked', { count: trackedUpcs.length })
                                : t('alerts.noChangesDetected', { min: sanitizeRange(threshold, maxChange).min, max: sanitizeRange(threshold, maxChange).max })
                            }
                        </p>
                    </div>
                )}

                {/* Alerts Content */}
                {!loading && !error && summary.total_alerts > 0 && (
                    <>
                        {/* Summary Bar */}
                        <div className="alerts-summary-bar">
                            <div className="summary-info">
                                <FaExclamationTriangle className="summary-icon" />
                                <span>
                                    <strong>{filteredCount}</strong> / {summary.total_alerts} {t('alerts.alerts')}
                                    ({t('alerts.changeBetween', { min: appliedRange.min, max: appliedRange.max })})
                                    <span className="basis-active-badge">
                                        {priceBasis === 'full' ? t('alerts.basisFull') : t('alerts.basisFinal')}
                                    </span>
                                    {filterByUpcs && <span className="filter-active-badge">{t('alerts.upcFilterActive')}</span>}
                                </span>
                            </div>
                            <div className="summary-actions">
                                <button
                                    className="filter-btn"
                                    onClick={downloadExcel}
                                    disabled={filteredCount === 0}
                                    title={t('alerts.downloadExcelHint')}
                                >
                                    <FaFileExcel />
                                    {t('alerts.downloadExcel')}
                                </button>
                                <button
                                    className="filter-btn"
                                    onClick={() => setShowChainFilter(!showChainFilter)}
                                >
                                    <FaFilter />
                                    {t('alerts.filterChannels')} ({selectedChains.size}/{Object.keys(alerts).length})
                                </button>
                            </div>
                        </div>

                        {/* Chain Filter Dropdown */}
                        {showChainFilter && (
                            <div className="chain-filter-panel">
                                <div className="filter-header">
                                    <span>{t('alerts.selectChannels')}</span>
                                    <div className="filter-actions">
                                        <button onClick={selectAllChains}>{t('alerts.all')}</button>
                                        <button onClick={deselectAllChains}>{t('alerts.none')}</button>
                                    </div>
                                </div>
                                <div className="filter-chains-grid">
                                    {Object.keys(alerts).sort().map(chain => {
                                        const chainInfo = summary.chains.find(c => c.name === chain) || {};
                                        const isSelected = selectedChains.has(chain);
                                        return (
                                            <label
                                                key={chain}
                                                className={`filter-chain-item ${isSelected ? 'selected' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleChainSelection(chain)}
                                                />
                                                {isSelected ? <FaCheckSquare className="check-icon" /> : <FaSquare className="check-icon" />}
                                                <span className="chain-label">{chain}</span>
                                                <span className="chain-badge" style={{ backgroundColor: chainInfo.color || '#7f8c8d' }}>
                                                    {alerts[chain]?.length || 0}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Vista Grid (tarjetas) - Ordenado globalmente por porcentaje */}
                        {viewMode === 'grid' && (
                        <div className="products-grid">
                            {filteredAlerts.map((product, idx) => {
                                const chainInfo = summary.chains.find(c => c.name === product.chainName) || {};

                                return (
                                    <div
                                        key={`${product.chainName}-${product.upc}-${idx}`}
                                        className="product-card clickable"
                                        onClick={() => loadPriceHistory(product, product.chainName)}
                                    >
                                        {/* Chain Badge */}
                                        <div
                                            className="product-chain-badge"
                                            style={{ backgroundColor: chainInfo.color || '#7f8c8d' }}
                                        >
                                            {product.chainName}
                                        </div>

                                        {/* Product Image */}
                                        <div className="product-image-container">
                                            {product.image ? (
                                                <img
                                                    src={product.image}
                                                    alt={product.item}
                                                    className="product-image"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div className="product-image-placeholder" style={{ display: product.image ? 'none' : 'flex' }}>
                                                <FaBell />
                                            </div>
                                        </div>

                                        {/* Product Info */}
                                        <div className="product-info">
                                            <h4 className="product-name" title={product.item}>
                                                {product.item?.length > 60
                                                    ? product.item.substring(0, 60) + '...'
                                                    : product.item}
                                            </h4>
                                            <span className="product-upc">UPC: {product.upc}</span>
                                        </div>

                                        {/* Price Section */}
                                        <div className="product-prices">
                                            <div className="price-row">
                                                <span className="price-label">{t('alerts.previous')}</span>
                                                <span className="price-value price-old">{formatPrice(product.last_price)}</span>
                                            </div>
                                            <div className="price-row">
                                                <span className="price-label">{t('alerts.current')}</span>
                                                <span className="price-value price-current">{formatPrice(product.final_price)}</span>
                                            </div>
                                            {/* Fecha de extracción */}
                                            {product.date && (
                                                <div className="price-row extraction-date">
                                                    <FaCalendarAlt className="date-icon" />
                                                    <span className="date-value">{formatExtractionDate(product.date)}</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Change Badge */}
                                        <div className={`price-change ${product.price_change_pct > 0 ? 'increase' : 'decrease'}`}>
                                            {product.price_change_pct > 0 ? <FaArrowUp /> : <FaArrowDown />}
                                            <span>{formatChange(product.price_change_pct)}</span>
                                        </div>

                                        {/* History indicator */}
                                        <div className="history-indicator">
                                            <FaHistory />
                                            <span>{t('alerts.viewHistory')}</span>
                                        </div>

                                        {/* Action */}
                                        {product.url_sku && (
                                            <a
                                                href={product.url_sku}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                className="product-link-btn"
                                            >
                                                <FaExternalLinkAlt />
                                                {t('alerts.viewInStore')}
                                            </a>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        )}

                        {/* Vista Tabla */}
                        {viewMode === 'table' && (
                            <div className="alerts-table-wrapper">
                                <table className="alerts-table">
                                    <thead>
                                        <tr>
                                            <th>{t('alerts.colChain')}</th>
                                            <th>{t('alerts.colProduct')}</th>
                                            <th>UPC</th>
                                            <th className="num">{t('alerts.colPrevious')}</th>
                                            <th className="num">{t('alerts.colCurrent')}</th>
                                            <th className="num">{t('alerts.colVariation')}</th>
                                            <th>{t('alerts.date')}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAlerts.map((product, idx) => {
                                            const chainInfo = summary.chains.find(c => c.name === product.chainName) || {};
                                            const up = product.price_change_pct > 0;
                                            return (
                                                <tr
                                                    key={`row-${product.chainName}-${product.upc}-${idx}`}
                                                    className="alerts-table-row"
                                                    onClick={() => loadPriceHistory(product, product.chainName)}
                                                >
                                                    <td>
                                                        <span
                                                            className="table-chain-badge"
                                                            style={{ backgroundColor: chainInfo.color || '#7f8c8d' }}
                                                        >
                                                            {product.chainName}
                                                        </span>
                                                    </td>
                                                    <td className="table-product">
                                                        {product.image ? (
                                                            <img
                                                                src={product.image}
                                                                alt={product.item}
                                                                className="table-product-img"
                                                                onError={(e) => { e.target.style.display = 'none'; }}
                                                            />
                                                        ) : null}
                                                        <span className="table-product-name" title={product.item}>
                                                            {product.item?.length > 55
                                                                ? product.item.substring(0, 55) + '...'
                                                                : product.item}
                                                        </span>
                                                    </td>
                                                    <td className="table-upc">{product.upc}</td>
                                                    <td className="num price-old">{formatPrice(product.last_price)}</td>
                                                    <td className="num price-current">{formatPrice(product.final_price)}</td>
                                                    <td className="num">
                                                        <span className={`table-change ${up ? 'increase' : 'decrease'}`}>
                                                            {up ? <FaArrowUp /> : <FaArrowDown />}
                                                            {formatChange(product.price_change_pct)}
                                                        </span>
                                                    </td>
                                                    <td className="table-date">{formatExtractionDate(product.date)}</td>
                                                    <td className="table-actions">
                                                        <button
                                                            className="table-history-btn"
                                                            title={t('alerts.viewHistory')}
                                                            onClick={(e) => { e.stopPropagation(); loadPriceHistory(product, product.chainName); }}
                                                        >
                                                            <FaHistory />
                                                        </button>
                                                        {product.url_sku && (
                                                            <a
                                                                href={product.url_sku}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="table-store-link"
                                                                title={t('alerts.viewInStore')}
                                                            >
                                                                <FaExternalLinkAlt />
                                                            </a>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Info Modal */}
            {showInfo && (
                <div className="modal-overlay" onClick={() => setShowInfo(false)}>
                    <div className="modal-content info-modal" onClick={e => e.stopPropagation()}>
                        <h3><FaInfoCircle /> {t('alerts.howToUse')}</h3>

                        <div className="info-section">
                            <h4>{t('alerts.overviewTitle')}</h4>
                            <p>{t('alerts.overviewDesc')}</p>
                        </div>

                        <div className="info-section">
                            <h4>{t('alerts.filterByUpcsTitle')}</h4>
                            <p>{t('alerts.filterByUpcsDesc')}</p>
                            <ol>
                                <li>{t('alerts.filterStep1')}</li>
                                <li>{t('alerts.filterStep2')}</li>
                                <li>{t('alerts.filterStep3')}</li>
                            </ol>
                        </div>

                        <button className="modal-close-btn" onClick={() => setShowInfo(false)}>
                            {t('alerts.understood')}
                        </button>
                    </div>
                </div>
            )}

            {/* UPC Manager Modal */}
            {showUpcManager && (
                <div className="modal-overlay" onClick={() => setShowUpcManager(false)}>
                    <div className="modal-content upc-modal" onClick={e => e.stopPropagation()}>
                        <h3><FaFileUpload /> {t('alerts.manageTrackedUpcs')}</h3>

                        {/* Estado del archivo */}
                        <div className={`file-status ${trackingFileExists ? 'exists' : 'not-exists'}`}>
                            {trackingFileExists ? (
                                <>
                                    <FaCheckCircle />
                                    <span>{t('alerts.fileExists', { count: trackedUpcs.length })}</span>
                                </>
                            ) : (
                                <>
                                    <FaTimesCircle />
                                    <span>{t('alerts.noTrackingFile')}</span>
                                </>
                            )}
                        </div>

                        {/* Error/Success Messages */}
                        {upcError && (
                            <div className="upc-message upc-error">
                                <FaExclamationTriangle />
                                <span>{upcError}</span>
                            </div>
                        )}
                        {upcSuccess && (
                            <div className="upc-message upc-success">
                                <FaCheckSquare />
                                <span>{upcSuccess}</span>
                            </div>
                        )}

                        {/* Current UPCs */}
                        {trackedUpcs.length > 0 && (
                            <div className="current-upcs">
                                <div className="current-upcs-header">
                                    <h4>{t('alerts.currentUpcs')} ({trackedUpcs.length})</h4>
                                    <button
                                        className="clear-upcs-btn"
                                        onClick={clearTrackedUpcs}
                                        disabled={savingUpcs}
                                        title={t('alerts.clearAllUpcs')}
                                    >
                                        <FaTrash /> {t('alerts.clear')}
                                    </button>
                                </div>
                                <div className="upcs-list">
                                    {trackedUpcs.slice(0, 20).map(upc => (
                                        <span key={upc} className="upc-tag">{upc}</span>
                                    ))}
                                    {trackedUpcs.length > 20 && (
                                        <span className="upc-tag more">+{trackedUpcs.length - 20} {t('alerts.more')}</span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* File Upload Section */}
                        <div className="file-upload-section">
                            <h4>{t('alerts.loadFromFile')}</h4>
                            <div className="file-buttons">
                                <button
                                    className="template-btn"
                                    onClick={downloadTemplate}
                                >
                                    <FaDownload /> {t('alerts.downloadTemplate')}
                                </button>
                                <button
                                    className="upload-btn"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={savingUpcs}
                                >
                                    <FaFileUpload /> {t('alerts.uploadFile')}
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv,.txt"
                                    onChange={handleFileUpload}
                                    style={{ display: 'none' }}
                                />
                            </div>
                        </div>

                        {/* Manual Entry Section */}
                        <div className="new-upcs-section">
                            <h4>{t('alerts.orEnterManually')}</h4>
                            <p className="hint">{t('alerts.upcsHint')}</p>
                            <textarea
                                value={newUpcs}
                                onChange={(e) => setNewUpcs(e.target.value)}
                                placeholder="7501082243741&#10;7501082243727&#10;7501082243710"
                                rows={6}
                                disabled={savingUpcs}
                            />
                        </div>

                        <div className="modal-actions">
                            <button
                                className="modal-btn-cancel"
                                onClick={() => setShowUpcManager(false)}
                                disabled={savingUpcs}
                            >
                                {t('alerts.cancel')}
                            </button>
                            {trackedUpcs.length > 0 && (
                                <button
                                    className="modal-btn-replace"
                                    onClick={() => saveTrackedUpcs(null, true)}
                                    disabled={!newUpcs.trim() || savingUpcs}
                                    title={t('alerts.replaceAllUpcs')}
                                >
                                    {savingUpcs ? t('common.loading') : t('alerts.replaceAll')}
                                </button>
                            )}
                            <button
                                className="modal-btn-save"
                                onClick={() => saveTrackedUpcs()}
                                disabled={!newUpcs.trim() || savingUpcs}
                            >
                                {savingUpcs ? t('common.loading') : (trackedUpcs.length > 0 ? t('alerts.addUpcs') : t('alerts.save'))}
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* History Modal */}
            {showHistoryModal && historyProduct && (
                <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
                    <div className="modal-content history-modal" onClick={e => e.stopPropagation()}>
                        <div className="history-modal-header">
                            <div className="history-header-info">
                                {historyProduct.image ? (
                                    <img
                                        src={historyProduct.image}
                                        alt={historyProduct.item}
                                        className="history-product-img"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                ) : (
                                    <FaChartLine className="history-icon" />
                                )}
                                <div>
                                    <h3>{t('alerts.priceHistory')}</h3>
                                    <p className="history-product-name">{historyProduct.item}</p>
                                    <span className="history-chain-badge" style={{ backgroundColor: summary.chains.find(c => c.name === historyProduct.chainName)?.color || '#7f8c8d' }}>
                                        {historyProduct.chainName}
                                    </span>
                                </div>
                            </div>
                            <button className="history-close-btn" onClick={() => setShowHistoryModal(false)}>
                                <FaTimes />
                            </button>
                        </div>

                        {historyLoading ? (
                            <div className="history-loading">
                                <div className="alerts-spinner"></div>
                                <span>{t('common.loading')}</span>
                            </div>
                        ) : priceHistory.length === 0 ? (
                            <div className="history-empty">
                                <FaHistory />
                                <p>{t('alerts.noHistoryAvailable')}</p>
                            </div>
                        ) : (
                            <>
                                {/* Gráfica de precios */}
                                <div className="history-chart">
                                    <h4><FaChartLine /> {t('alerts.priceEvolution')}</h4>
                                    <div style={{ height: '200px' }}>
                                        <Line
                                            data={{
                                                labels: priceHistory.map(h => formatShortDate(h.date)),
                                                datasets: [{
                                                    label: 'Precio Final',
                                                    data: priceHistory.map(h => h.final_price),
                                                    borderColor: '#073C5C',
                                                    backgroundColor: 'rgba(7, 60, 92, 0.1)',
                                                    borderWidth: 2,
                                                    pointBackgroundColor: '#073C5C',
                                                    pointBorderColor: '#fff',
                                                    pointBorderWidth: 2,
                                                    pointRadius: 5,
                                                    pointHoverRadius: 7,
                                                    pointHoverBackgroundColor: '#F68D2E',
                                                    tension: 0.3,
                                                    fill: true
                                                }]
                                            }}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: { display: false },
                                                    tooltip: {
                                                        callbacks: {
                                                            label: (context) => `Precio: ${formatPrice(context.raw)}`
                                                        }
                                                    }
                                                },
                                                scales: {
                                                    y: {
                                                        beginAtZero: true,
                                                        ticks: {
                                                            callback: (value) => `$${value}`,
                                                            font: { size: 11 },
                                                            color: '#6b7280'
                                                        },
                                                        grid: { color: '#e5e7eb' }
                                                    },
                                                    x: {
                                                        ticks: {
                                                            font: { size: 11 },
                                                            color: '#6b7280'
                                                        },
                                                        grid: { display: false }
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Tabla de historial */}
                                <div className="history-table-container">
                                    <h4><FaHistory /> {t('alerts.lastRecords', { count: priceHistory.length })}</h4>
                                    <table className="history-table">
                                        <thead>
                                            <tr>
                                                <th>{t('alerts.date')}</th>
                                                <th>{t('alerts.regularPrice')}</th>
                                                <th>{t('alerts.salePrice')}</th>
                                                <th>{t('alerts.finalPrice')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[...priceHistory].reverse().map((record, idx) => (
                                                <tr key={idx} className={idx === 0 ? 'current-row' : ''}>
                                                    <td className="date-cell">
                                                        <FaCalendarAlt />
                                                        {formatExtractionDate(record.date)}
                                                    </td>
                                                    <td>{formatPrice(record.price)}</td>
                                                    <td>{record.sale_price ? formatPrice(record.sale_price) : '-'}</td>
                                                    <td className="final-price-cell">{formatPrice(record.final_price)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Info del producto actual */}
                                <div className="history-current-info">
                                    <div className="current-price-box">
                                        <span className="label">{t('alerts.currentPrice')}</span>
                                        <span className="value">{formatPrice(historyProduct.final_price)}</span>
                                    </div>
                                    <div className="current-change-box">
                                        <span className="label">{t('alerts.detectedChange')}</span>
                                        <span className={`value ${historyProduct.price_change_pct > 0 ? 'increase' : 'decrease'}`}>
                                            {historyProduct.price_change_pct > 0 ? <FaArrowUp /> : <FaArrowDown />}
                                            {formatChange(historyProduct.price_change_pct)}
                                        </span>
                                    </div>
                                    <div className="current-upc-box">
                                        <span className="label">UPC</span>
                                        <span className="value">{historyProduct.upc}</span>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Link a tienda */}
                        {historyProduct.url_sku && (
                            <a
                                href={historyProduct.url_sku}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="history-store-link"
                            >
                                <FaExternalLinkAlt />
                                {t('alerts.viewInStore')}
                            </a>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AlertsPage;
